import { ref } from "vue";
import { useOidcStore } from "vue3-oidc";

const TERMINAL_ERRORS = new Set([
  "invalid_grant",
  "invalid_client",
  "login_required",
  "interaction_required",
  "consent_required",
]);

const KEYCLOAK_PROBE_TIMEOUT_MS = 5000;

const isTerminal = (error) => TERMINAL_ERRORS.has(error?.error ?? error?.name);

// Session Keycloak expirée : on garde l'utilisateur et on laisse l'utilisateur
// choisir quand se reconnecter, pour ne pas perdre une saisie en cours.
export const sessionExpired = ref(false);

const isKeycloakReachable = async (userManager) => {
  const authority = userManager.settings.authority.replace(/\/$/, "");
  try {
    const response = await fetch(
      `${authority}/.well-known/openid-configuration`,
      {
        cache: "no-store",
        signal: AbortSignal.timeout(KEYCLOAK_PROBE_TIMEOUT_MS),
      }
    );
    return response.ok;
  } catch {
    return false;
  }
};

let restarting = false;

// Renvoie false si Keycloak ne répond pas : l'utilisateur reste dans l'app.
export const reconnect = async () => {
  const userManager = useOidcStore().state.value.userManager;
  if (!userManager || restarting) return true;
  if (!navigator.onLine || !(await isKeycloakReachable(userManager)))
    return false;

  restarting = true;
  try {
    await userManager.signinRedirect({
      state: {
        redirectPath: globalThis.location.pathname + globalThis.location.search,
      },
    });
    return true;
  } catch (error) {
    console.error("OIDC reconnexion impossible", error);
    restarting = false;
    return false;
  }
};

const handleFailure = async (error, userManager, user) => {
  console.error("OIDC renouvellement impossible", {
    error: error?.error ?? error?.name,
    message: error?.message,
    hasRefreshToken: Boolean(user?.refresh_token),
    expiresAt: user?.expires_at,
  });
  if (!isTerminal(error)) return;
  const stored = await userManager.getUser();
  if (stored && !stored.expired) return;
  sessionExpired.value = true;
};

export const handleSilentRenewError = async (error) => {
  const { state } = useOidcStore();
  const userManager = state.value.userManager;
  if (!userManager) return;
  await handleFailure(error, userManager, state.value.user);
};

let renewal = null;

const renewIfExpired = async (force) => {
  const { state } = useOidcStore();
  const userManager = state.value.userManager;
  if (!userManager || !navigator.onLine) return;

  const user = await userManager.getUser();
  if (!user) return;
  if (!force && !user.expired) {
    // Jeton renouvelé par un autre onglet.
    if (user.access_token !== state.value.token) syncOidcUser(user);
    return;
  }

  try {
    await userManager.signinSilent();
  } catch (error) {
    await handleFailure(error, userManager, user);
  }
};

const renewSession = (force = false) => {
  renewal =
    renewal ??
    renewIfExpired(force).finally(() => {
      renewal = null;
    });
  return renewal;
};

export const ensureLiveSession = () => renewSession();

export const handleAccessTokenExpired = () => renewSession();

// Le back a refusé le jeton : on le renouvelle même s'il n'est pas expiré
// côté navigateur (horloge décalée, session révoquée).
export const renewAfterUnauthorized = () => renewSession(true);

export const syncOidcUser = (user) => {
  useOidcStore().state.value.user = user;
  if (user && !user.expired) sessionExpired.value = false;
};

export const watchSessionRecovery = () => {
  globalThis.addEventListener("online", () => renewSession());
  globalThis.document?.addEventListener("visibilitychange", () => {
    if (globalThis.document.visibilityState === "visible") renewSession();
  });
};
